import React, { forwardRef, useImperativeHandle, useMemo, useState, useEffect } from 'react';
import { Box, Flex, Button, Input, Switch, Text } from '@chakra-ui/core';
import { Column, Row } from 'react-table';
import { DistanceUnit, RowBase, PaneHandle, PaneProps } from '../accountParameter.types';
import { DataTable } from './DataTable';
import colors from '../../../../styles/colors';
import { PRIMARY_BLUE, PRIMARY_BLUE_HOVER, SEGMENT_BG, SEGMENT_BUTTON_BASE, TEXT_INPUT_FIELD_STYLES } from '../constants';
import { useStoreState } from '../../../../models/hooks';
import { useTranslation } from 'react-i18next';

import { useSettingsDisabled } from '../SettingsDisabledContext';

interface PositionMiningState {
  enabled: boolean;
  unit: DistanceUnit;
  radius: string;
}

export const PositionMiningPane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty }, ref) => {
  const { t } = useTranslation('accountParameter');
  const account = useStoreState((state) => state.app.accounts);
  const disabled = useSettingsDisabled();

  const accountItems = useMemo(() => {
    return {
      enabled: account?.configuration?.positionMiningEnabled ?? false,
      unit: 'KM' as DistanceUnit, // Default to KM, as it's not in account config
      radius: String(account?.configuration?.positionMiningRadius ?? ''),
    };
  }, [account]);

  const [initialState, setInitialState] = useState<PositionMiningState>(accountItems);
  const [enabled, setEnabled] = useState(initialState.enabled);
  const [unit, setUnit] = useState<DistanceUnit>(initialState.unit);
  const [radius, setRadius] = useState(initialState.radius);

  const stateRef = React.useRef({ enabled, unit, radius, setEnabled, setUnit, setRadius });
  stateRef.current = { enabled, unit, radius, setEnabled, setUnit, setRadius };

  useEffect(() => {
    setInitialState(accountItems);
    setEnabled(accountItems.enabled);
    setUnit(accountItems.unit);
    setRadius(accountItems.radius);
  }, [accountItems]);

  useEffect(() => {
    const dirty =
      enabled !== initialState.enabled || unit !== initialState.unit || String(radius) !== String(initialState.radius);
    setDirty(dirty);
  }, [enabled, unit, radius, initialState, setDirty]);

  useImperativeHandle(ref, () => ({
    save: async () => {
      const updates: Record<string, any> = {
        positionMiningEnabled: enabled,
      };

      if (enabled) {
        const num = Number(radius);
        const radiusKm = unit === 'Miles' ? Math.round(num * 1.60934 * 100) / 100 : num;
        updates.positionMiningRadius = radiusKm;
      }

      const success = await onSave(updates);
      if (success) {
        const newInitialState = { enabled, unit, radius: String(radius) };
        setInitialState(newInitialState);
      }
    },
    reset: () => {
      setEnabled(initialState.enabled);
      setUnit(initialState.unit);
      setRadius(initialState.radius);
    },
  }));

  type MiningRow = RowBase & { type: 'switch' | 'radius' };
  const data: MiningRow[] = useMemo(
    () => [
      {
        key: 'positionMiningEnabled',
        label: t('positionMiningPane.positionMiningEnabled'),
        description: t('positionMiningPane.positionMiningEnabledDescription'),
        type: 'switch',
      },
      ...(enabled
        ? [
            {
              key: 'positionMiningRadius',
              label: t('positionMiningPane.positionMiningRadius'),
              description: t('positionMiningPane.positionMiningRadiusDescription'),
              type: 'radius' as const,
            },
          ]
        : []),
    ],
    [enabled, t],
  );

  const columns = useMemo<Column<MiningRow>[]>(
    () => [
      {
        Header: t('genericSettingsPane.parameter') as string,
        accessor: 'label',
        Cell: ({ row, value }: { row: Row<MiningRow>; value: string }) => {
          const isChild = row.original.type === 'radius';
          return (
            <Box pl={isChild ? 6 : 0} borderLeft={isChild ? '2px solid #E2E8F0' : 'none'}>
              <Flex align="center">
                {isChild && (
                  <Box as="span" aria-hidden mr={2} color={colors.neutralGray[500]}>
                    ↳
                  </Box>
                )}
                <Text fontWeight={600} mr={isChild ? 2 : 3}>
                  {value}
                </Text>
                {!isChild && (
                  <Box
                    as="span"
                    ml={2}
                    px={2}
                    py={0.5}
                    fontSize="10px"
                    borderRadius="8px"
                    bg="#F1F5F9"
                    color={colors.neutralGray[600]}
                  >
                    {t('positionMiningPane.group')}
                  </Box>
                )}
              </Flex>
            </Box>
          );
        },
      },
      {
        Header: t('genericSettingsPane.description') as string,
        accessor: 'description',
        disableSortBy: true,
        Cell: ({ value }: { value: string }) => <Text fontSize="xs">{value}</Text>,
      },
      {
        Header: t('genericSettingsPane.value') as string,
        id: 'value',
        disableSortBy: true,
        Cell: ({ row }: { row: Row<MiningRow> }) => {
          const { enabled, unit, radius, setEnabled, setUnit, setRadius } = stateRef.current;
          const isChild = row.original.type === 'radius';
          if (!isChild) {
            return <Switch isChecked={enabled} onChange={(e) => setEnabled(e.target.checked)} size="md" isDisabled={disabled} />;
          }
          const isInputDisabled = !enabled || disabled;
          return (
            <Flex align="center" pl={6} opacity={enabled ? 1 : 0.6}>
              <Input
                type="number"
                inputMode="numeric"
                step="1"
                min={0}
                placeholder={enabled ? t('positionMiningPane.inputRadius') : t('positionMiningPane.enablePositionMiningToSet')}
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                isReadOnly={isInputDisabled}
                autoFocus={enabled}
                {...TEXT_INPUT_FIELD_STYLES}
                maxW="260px"
                mr="12px"
              />
              <Flex bg={SEGMENT_BG} borderRadius="8px" p="4px">
                <Button
                  {...SEGMENT_BUTTON_BASE}
                  mr="6px"
                  bg={unit === 'KM' ? PRIMARY_BLUE : SEGMENT_BG}
                  color={unit === 'KM' ? '#FFFFFF' : colors.neutralGray[700]}
                  _hover={{ bg: unit === 'KM' ? PRIMARY_BLUE_HOVER : SEGMENT_BG }}
                  isDisabled={isInputDisabled}
                  onClick={() => setUnit('KM')}
                >
                  {t('positionMiningPane.km')}
                </Button>
                <Button
                  {...SEGMENT_BUTTON_BASE}
                  bg={unit === 'Miles' ? PRIMARY_BLUE : SEGMENT_BG}
                  color={unit === 'Miles' ? '#FFFFFF' : colors.neutralGray[700]}
                  _hover={{ bg: unit === 'Miles' ? PRIMARY_BLUE_HOVER : SEGMENT_BG }}
                  isDisabled={isInputDisabled}
                  onClick={() => setUnit('Miles')}
                >
                  {t('positionMiningPane.miles')}
                </Button>
              </Flex>
            </Flex>
          );
        },
      },
    ],
    [t, disabled],
  );

  const getRowProps = (row: Row<MiningRow>) => {
    const { key } = row.original;
    let isDirty = false;
    if (key === 'positionMiningEnabled') {
      isDirty = enabled !== initialState.enabled;
    } else if (key === 'positionMiningRadius') {
      isDirty = radius !== initialState.radius || unit !== initialState.unit;
    }
    return { className: isDirty ? 'dirty' : '' };
  };

  return <DataTable columns={columns} data={data} getRowProps={getRowProps} />;
});
